import api from "@/api/api";
import cacheService from "./cache.service";

interface PhotoCacheKey {
  type: "agco" | "supplier";
  page: number;
  perPage: number;
  group?: string;
}

class PhotoCacheService {
  private static instance: PhotoCacheService;
  private readonly CACHE_EXPIRY_HOURS = 5;

  private constructor() {
    // Nettoyer le cache au démarrage
    this.cleanup();
  }

  static getInstance(): PhotoCacheService {
    if (!PhotoCacheService.instance) {
      PhotoCacheService.instance = new PhotoCacheService();
    }
    return PhotoCacheService.instance;
  }

  /**
   * Génère une clé de cache pour les photos
   */
  private generateCacheKey(params: PhotoCacheKey): string {
    const { type, page, perPage, group } = params;
    return `photos_${type}_page${page}_per${perPage}${
      group ? `_group${group}` : ""
    }`;
  }

  /**
   * Récupère les photos AGCO avec cache
   */
  async getAgcoPhotos(page: number = 1, perPage: number = 50, group?: string) {
    const cacheKey = this.generateCacheKey({
      type: "agco",
      page,
      perPage,
      group,
    });

    // Vérifier le cache d'abord
    const cached = cacheService.get(cacheKey);
    if (cached) {
      return cached;
    }

    // Si pas en cache, faire la requête API
    try {
      const params: any = { page, per_page: perPage };
      if (group) params.group = group;

      const response = await api.get("/product_images/agco/products", {
        params,
      });

      const data = response.data;

      // Stocker en cache pour 5 heures
      cacheService.set(cacheKey, data, this.CACHE_EXPIRY_HOURS);

      return data;
    } catch (error) {
      console.error("Erreur lors de la récupération des photos AGCO:", error);
      throw error;
    }
  }

  /**
   * Récupère les photos des fournisseurs avec cache
   */
  async getSupplierPhotos(
    page: number = 1,
    perPage: number = 50,
    group?: string
  ) {
    const cacheKey = this.generateCacheKey({
      type: "supplier",
      page,
      perPage,
      group,
    });

    // Vérifier le cache d'abord
    const cached = cacheService.get(cacheKey);
    if (cached) {
      return cached;
    }

    // Si pas en cache, faire la requête API
    try {
      const params: any = { page, per_page: perPage };
      if (group) params.group = group;

      const response = await api.get("/product_images/supplier/products", {
        params,
      });

      const data = response.data;

      // Stocker en cache pour 5 heures
      cacheService.set(cacheKey, data, this.CACHE_EXPIRY_HOURS);

      return data;
    } catch (error) {
      console.error(
        "Erreur lors de la récupération des photos fournisseurs:",
        error
      );
      throw error;
    }
  }

  /**
   * Invalide le cache pour un type spécifique
   */
  invalidateCache(type: "agco" | "supplier" | "all") {
    try {
      const keys = Object.keys(localStorage);
      const cacheKeys = keys.filter((key) =>
        key.includes("agco_lisaprint_cache_photos_")
      );

      cacheKeys.forEach((key) => {
        if (type === "all" || key.includes(`photos_${type}`)) {
          const originalKey = key.replace("agco_lisaprint_cache_", "");
          cacheService.remove(originalKey);
        }
      });
    } catch (error) {
      console.error("Erreur lors de l'invalidation du cache:", error);
    }
  }

  /**
   * Précache plusieurs pages pour améliorer les performances
   */
  async preloadCache(
    type: "agco" | "supplier",
    pages: number[] = [1, 2, 3],
    perPage: number = 50
  ) {
    const promises = pages.map((page) => {
      if (type === "agco") {
        return this.getAgcoPhotos(page, perPage);
      } else {
        return this.getSupplierPhotos(page, perPage);
      }
    });

    try {
      await Promise.all(promises);
    } catch (error) {
      console.error(`Erreur lors du préchargement pour ${type}:`, error);
    }
  }

  /**
   * Nettoie le cache expiré
   */
  cleanup() {
    cacheService.cleanup();
  }

  /**
   * Obtient les statistiques du cache
   */
  getCacheStats() {
    return cacheService.getInfo();
  }

  /**
   * Vide tout le cache des photos
   */
  clearAll() {
    this.invalidateCache("all");
  }
}

export default PhotoCacheService.getInstance();
